import { Paper, Group, Box, Text, ActionIcon, Tooltip } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import type { ModelViewModel } from "@models/model";
import { extractTiers, formatTierSummary } from "../../utils/modelModalHelper";

interface ModelCardProps {
    model: ModelViewModel;
    deletingId: string | null;
    onEdit: (m: ModelViewModel) => void;
    onDelete: (id: string) => void;
    onToggleStatus: (m: ModelViewModel) => void;
}

export const ModelCard = ({
    model: m,
    deletingId,
    onEdit,
    onDelete,
    onToggleStatus,
}: ModelCardProps) => {
    const cfg = m.costInfo?.costConfig ?? {};
    const currency = m.costInfo?.currency ?? "USD";
    const inputTiers = extractTiers(cfg, "input_tokens");
    const outputTiers = extractTiers(cfg, "output_tokens");
    const hasPricing = inputTiers.length > 0 || outputTiers.length > 0;

    return (
        <Paper p="sm" withBorder radius="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={500}>
                        {m.modelName}
                    </Text>
                    {m.displayName && (
                        <Text size="xs" c="dimmed">
                            {m.displayName}
                        </Text>
                    )}
                    {hasPricing && (
                        <Group gap="lg" mt={4}>
                            {inputTiers.length > 0 && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Input
                                    </Text>
                                    <Text size="xs" fw={500}>
                                        {formatTierSummary(inputTiers, currency)}
                                    </Text>
                                </Box>
                            )}
                            {outputTiers.length > 0 && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Output
                                    </Text>
                                    <Text size="xs" fw={500}>
                                        {formatTierSummary(outputTiers, currency)}
                                    </Text>
                                </Box>
                            )}
                        </Group>
                    )}
                    {Object.keys(m.modelMetadata ?? {}).length > 0 && (
                        <Text size="xs" c="dimmed" mt={2}>
                            {Object.entries(m.modelMetadata)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(" · ")}
                        </Text>
                    )}
                </Box>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Badge
                        size="xs"
                        color={m.status === "active" ? "green" : "gray"}
                        style={{ cursor: "pointer" }}
                        onClick={() => onToggleStatus(m)}
                    >
                        {m.status}
                    </Badge>
                    <Tooltip label="Edit">
                        <ActionIcon size="sm" variant="subtle" onClick={() => onEdit(m)}>
                            <IconEdit size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete">
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            loading={deletingId === m.id}
                            onClick={() => onDelete(m.id)}
                        >
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>
        </Paper>
    );
};
